<template lang="html">
  <div class="range-slider"
  :style="{ 'width': ((2 * margin) + finalWidth) + 'px' }">
    <div v-if="title" class="range-slider-title mb-2">{{ title }}</div>
    <div class="mb-3">
      <span>
        <span class="range-label">{{ minLabel }}</span>
        <span class="range-unit-label">{{ unitLabel }}</span>
      </span>
      <span class="mh2">-</span>
      <span>
        <span class="range-label">{{ maxLabel }}</span>
        <span class="range-unit-label">{{ unitLabel }}</span>
      </span>
    </div>
    <div class="slider-container position-relative"
      :style="{ 'height': (2 * margin) + 'px' }"
      @click="snapNearestControl($event)"
      ref="slider"
    >
      <div class="slider-control position-absolute"
        :style="{
          'left': (min.pos + margin) + 'px',
          'height': (2 * margin) + 'px',
          'width': (2 * margin) + 'px'
        }"
        @mousedown="moveStart($event, 0)"
        @touchstart="moveStart($event, 0)"
        @click="$event.preventDefault()"
      />
      <div class="slider-control position-absolute"
        :style="{
          'left': (max.pos + margin) + 'px',
          'height': (2 * margin) + 'px',
          'width': (2 * margin) + 'px'
        }"
        @mousedown="moveStart($event, 1)"
        @touchstart="moveStart($event, 1)"
        @click="$event.preventDefault()"
      />
      <div class="slider-bg position-relative w-100"
        :style="{ 'width': (finalWidth + margin * 2) + 'px' }"
      />
      <div class="slider position-absolute"
        :style="{
          'width': posDifference + 'px',
          'left':  (min.pos + margin) + 'px'
        }"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  props: {
    title: {
      type: String,
      default: ''
    },
    defaultRange: {
      type: Object,
      required: true,
      default: () => ({
        min: 0,
        max: 9999
      })
    },
    range: {
      type: Object,
      required: true,
      default: () => Object.assign({}, this.defaultRange)
    },
    unitLabel: {
      type: String,
      default: ''
    },
    width: {
      type: Number,
      default: 150
    },
    margin: {
      type: Number,
      default: 14
    },
    minDistance: {
      type: Number,
      default: 50
    },
    increments: {
      type: Number,
      default: 1
    }
  },
  data() {
    return {
      startX: 0,
      min: {
        drag: false,
        pos: 0
      },
      max: {
        drag: false,
        pos: this.width
      }
    };
  },
  created() {
    this.min.pos = this.convertRangeToPos(this.range.min);
    this.max.pos = this.convertRangeToPos(this.range.max);
  },
  computed: {
    ...mapGetters({
      deviceMd: 'ui/deviceMd'
    }),
    finalWidth() {
      if (this.deviceMd) {
        return this.width;
      }

      return window.innerWidth - 60;
    },
    minLabel() {
      return Math.round(
        this.convertPosToRange(this.min.pos) / this.increments
      ) * this.increments;
    },
    maxLabel() {
      return Math.round(
        this.convertPosToRange(this.max.pos) / this.increments
      ) * this.increments;
    },
    rangeDifference() {
      return Math.abs(this.defaultRange.max - this.defaultRange.min);
    },
    posDifference() {
      return Math.abs(this.max.pos - this.min.pos);
    }
  },
  watch: {
    range(newRange) {
      this.min.pos = this.convertRangeToPos(newRange.min);
      this.max.pos = this.convertRangeToPos(newRange.max);
    }
  },
  methods: {
    convertPosToRange(pos) {
      return Math.floor(
        this.defaultRange.min
        + ((this.rangeDifference / this.finalWidth) * pos)
      );
    },
    convertRangeToPos(range) {
      return (
        (this.finalWidth / this.rangeDifference)
        * (range - this.defaultRange.min)
      );
    },
    moveStart(e, index = 0) {
      let pointer = e;
      if ('touches' in e) pointer = e.touches[0];
      if (index === 0) this.min.drag = true;
      else if (index === 1) this.max.drag = true;
      this.startX = pointer.clientX;
      document.addEventListener('mousemove', this.move);
      document.addEventListener('mouseup', this.moveEnd);
      document.addEventListener('touchmove', this.move);
      document.addEventListener('touchend', this.moveEnd);
    },
    move(e) {
      let pointer = e;
      if ('touches' in e) pointer = e.touches[0];

      const movementX = pointer.clientX - this.startX;

      if (this.min.drag) {
        if ((this.min.pos + movementX) < 0) {
          this.min.pos = 0;
        } else if ((this.min.pos + movementX) > (this.max.pos - this.minDistance)) {
          this.min.pos = this.max.pos - this.minDistance;
        } else {
          this.min.pos += movementX;
          this.startX = pointer.clientX;
        }
      } else if (this.max.drag) {
        if ((this.max.pos + movementX) < (this.min.pos + this.minDistance)) {
          this.max.pos = this.min.pos + this.minDistance;
        } else if ((this.max.pos + movementX) > this.finalWidth) {
          this.max.pos = this.finalWidth;
        } else {
          this.max.pos += movementX;
          this.startX = pointer.clientX;
        }
      }
    },
    moveEnd() {
      this.min.drag = false;
      this.max.drag = false;
      document.removeEventListener('mousemove', this.move);
      document.removeEventListener('mouseup', this.moveEnd);
      document.removeEventListener('touchmove', this.move);
      document.removeEventListener('touchend', this.moveEnd);
      this.updateRange();
    },
    snapNearestControl(e) {
      const sliderClientX =
        Math.floor(this.$refs.slider.getBoundingClientRect().left);
      const offsetX = e.clientX - sliderClientX - this.margin;
      if (offsetX < 0) {
        this.min.pos = 0;
      } else if (offsetX > this.finalWidth) {
        this.max.pos = this.finalWidth;
      } else if (Math.abs(offsetX - this.max.pos)
        < Math.abs(offsetX - this.min.pos)) {
        if (offsetX < (this.min.pos + this.minDistance)) {
          this.max.pos = this.min.pos + this.minDistance;
        } else {
          this.max.pos = offsetX;
        }
        this.updateRange();
      } else {
        if (offsetX > (this.max.pos - this.minDistance)) {
          this.min.pos = offsetX;
        } else {
          this.min.pos = offsetX;
        }
        this.updateRange();
      }
    },
    updateRange() {
      this.$emit('change', {
        min: this.convertPosToRange(this.min.pos),
        max: this.convertPosToRange(this.max.pos)
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.range-slider {
  text-align: left;
}

.range-label {
  font-family: Scandia-Regular, sans-serif;
  font-size: 1.3rem;
  color: var(--text-dark);
}

.slider-control {
  transform: translateX(-50%);
  cursor: pointer;
  top: 0;
  border-radius: 999px;
  z-index: 5;
  border-color: var(--action);
  border-style: solid;
  border-width: 2px;
  background: white;

  &:hover, &:active {
    background-color: var(--action);
  }
}

.slider-bg {
  background: hsl(0, 0%, 90%);
  height: 2px;
  top: 50%;
  transform: translateY(-1px);
  z-index: 3;
}

.slider {
  height: 2px;
  top: 50%;
  transform: translateY(-1px);
  background: var(--action);
  z-index: 4;
}
</style>



// WEBPACK FOOTER //
// src/components/input/RangeSlider.vue